/**
 * AdminPagamentos — /admin/pagamentos
 * Painel único onde os administradores configuram:
 *   - Stripe (test/live + secret key + webhook secret)
 *   - PayPal (sandbox/live + client_id + secret)
 *   - ifthenpay (gateway + anti-phishing + MB WAY + Multibanco)
 *   - Transferência bancária (IBAN, BIC, beneficiário)
 *   - Pagar mais tarde (validade + lembretes)
 *
 * Regras:
 *   - Valores gravados aqui SOBREPÕEM-SE ao .env (backend prioridade DB→ENV).
 *   - Secrets nunca são devolvidos em plain — só um preview dos últimos 4 chars.
 *   - Deixar um secret VAZIO no form == manter o existente (não apaga).
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, RefreshCw, Check, X, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

const SECRET_HINT = "Deixa vazio para manter a chave atual";

function SecretInput({ value, onChange, preview, configured, placeholder, testId }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <Input type="password" value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || SECRET_HINT} data-testid={testId} className="font-mono" />
        {configured && (
          <span className="text-xs text-emerald-700 whitespace-nowrap">
            ••••{preview}
            <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-100 border border-emerald-300 text-[10px] uppercase font-bold">Configurada</span>
          </span>
        )}
      </div>
    </div>
  );
}

function GatewayCard({ title, subtitle, accent, enabled, onToggleEnabled, children, onTest, testTestId }) {
  return (
    <div className="rounded-xl border-2 p-4 bg-white shadow-sm" style={{ borderColor: accent }}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-[Fredoka] font-bold text-lg text-slate-900">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={!!enabled} onChange={(e) => onToggleEnabled(e.target.checked)}
            data-testid={testTestId ? `${testTestId}-enabled` : undefined} className="w-4 h-4 accent-emerald-600" />
          <span className={`text-xs font-bold uppercase ${enabled ? "text-emerald-700" : "text-slate-400"}`}>Ativo</span>
        </label>
      </div>
      <div className="space-y-3">{children}</div>
      {onTest && (
        <Button variant="outline" onClick={onTest} className="mt-3 w-full rounded-full" data-testid={testTestId ? `${testTestId}-test` : undefined}>
          <Zap className="w-4 h-4 mr-1.5" /> Testar ligação
        </Button>
      )}
    </div>
  );
}

function ModeToggle({ value, onChange, options, testId }) {
  return (
    <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-full p-0.5" data-testid={testId}>
      {options.map((o) => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
            value === o.value ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:text-slate-900"
          }`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function AdminPagamentos() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState(null);
  // Secrets typed by the admin in this session — sent to the API on Save.
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payment-settings");
      setState(data);
      setDrafts({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a carregar configuração");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setSection = (section, field, value) => {
    setState((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), [field]: value } }));
  };
  const setDraftSecret = (section, field, value) => {
    setDrafts((prev) => ({ ...prev, [`${section}.${field}`]: value }));
  };

  const save = async () => {
    if (!state) return;
    setSaving(true);
    try {
      const payload = {};
      for (const section of ["stripe", "paypal", "ifthenpay", "bank_transfer", "pay_later"]) {
        const src = state[section] || {};
        const out = {};
        for (const [k, v] of Object.entries(src)) {
          if (k.endsWith("_configured") || k.endsWith("_preview")) continue;
          out[k] = v;
        }
        // Merge in typed secrets — only fields the admin actually filled in.
        for (const [key, val] of Object.entries(drafts)) {
          const [s, f] = key.split(".");
          if (s === section && val) out[f] = val;
        }
        payload[section] = out;
      }
      const { data } = await api.put("/admin/payment-settings", payload);
      setState(data);
      setDrafts({});
      toast.success("Configuração guardada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a guardar");
    } finally {
      setSaving(false);
    }
  };

  const test = async (provider) => {
    try {
      const { data } = await api.post(`/admin/payment-settings/test/${provider}`);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a testar");
    }
  };

  const runReminders = async () => {
    try {
      const { data } = await api.post("/admin/pay-later/run-reminders");
      toast.success(`Lembretes enviados: ${data.sent} · saltados: ${data.skipped}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a correr lembretes");
    }
  };

  const originRows = useMemo(() => {
    if (!state?.origin) return [];
    return Object.entries(state.origin).sort((a, b) => a[0].localeCompare(b[0]));
  }, [state]);

  if (loading) return <div className="p-8 text-center text-slate-500">A carregar…</div>;
  if (!state) return null;

  const stripe = state.stripe || {};
  const paypal = state.paypal || {};
  const ifthenpay = state.ifthenpay || {};
  const bank = state.bank_transfer || {};
  const later = state.pay_later || {};

  return (
    <div className="p-6 max-w-7xl mx-auto text-slate-900" data-testid="admin-pagamentos">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-[Fredoka] font-bold">Pagamentos</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Gere as credenciais dos providers diretamente daqui. Os valores aqui guardados sobrepõem-se
            a qualquer chave em <code>.env</code>. As chaves secretas nunca são devolvidas pelo servidor —
            só vês um preview com os últimos 4 caracteres.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="rounded-full" data-testid="btn-reload">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Recarregar
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-slate-900 hover:bg-black" data-testid="btn-save-payments">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "A guardar…" : "Guardar alterações"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Stripe */}
        <GatewayCard
          title="Stripe"
          subtitle="Cartões, Apple/Google Pay (via Checkout)"
          accent="#8b5cf6"
          enabled={stripe.enabled}
          onToggleEnabled={(v) => setSection("stripe", "enabled", v)}
          onTest={() => test("stripe")}
          testTestId="stripe"
        >
          <div>
            <Label className="text-xs">Modo</Label>
            <ModeToggle value={stripe.mode || "test"} onChange={(v) => setSection("stripe", "mode", v)}
              options={[{ value: "test", label: "TEST" }, { value: "live", label: "LIVE" }]}
              testId="stripe-mode" />
          </div>
          <div>
            <Label className="text-xs">Stripe Secret Key</Label>
            <SecretInput
              value={drafts["stripe.secret_key"] || ""} onChange={(v) => setDraftSecret("stripe", "secret_key", v)}
              preview={stripe.secret_key_preview} configured={stripe.secret_key_configured}
              placeholder="sk_test_… ou sk_live_…" testId="stripe-secret-key" />
            <p className="text-[10px] text-slate-500 mt-1">Dashboard Stripe → Developers → API keys</p>
          </div>
          <div>
            <Label className="text-xs">Webhook Secret</Label>
            <SecretInput
              value={drafts["stripe.webhook_secret"] || ""} onChange={(v) => setDraftSecret("stripe", "webhook_secret", v)}
              preview={stripe.webhook_secret_preview} configured={stripe.webhook_secret_configured}
              placeholder="whsec_…" testId="stripe-webhook-secret" />
          </div>
        </GatewayCard>

        {/* PayPal */}
        <GatewayCard
          title="PayPal"
          subtitle="Conta PayPal e cartões via PayPal Smart Buttons"
          accent="#0f6dc9"
          enabled={paypal.enabled}
          onToggleEnabled={(v) => setSection("paypal", "enabled", v)}
          onTest={() => test("paypal")}
          testTestId="paypal"
        >
          <div>
            <Label className="text-xs">Modo</Label>
            <ModeToggle value={paypal.mode || "sandbox"} onChange={(v) => setSection("paypal", "mode", v)}
              options={[{ value: "sandbox", label: "SANDBOX" }, { value: "live", label: "LIVE" }]}
              testId="paypal-mode" />
          </div>
          <div>
            <Label className="text-xs">PayPal Client ID</Label>
            <SecretInput
              value={drafts["paypal.client_id"] || ""} onChange={(v) => setDraftSecret("paypal", "client_id", v)}
              preview={paypal.client_id_preview} configured={paypal.client_id_configured}
              placeholder="ATB..." testId="paypal-client-id" />
          </div>
          <div>
            <Label className="text-xs">PayPal Secret</Label>
            <SecretInput
              value={drafts["paypal.secret"] || ""} onChange={(v) => setDraftSecret("paypal", "secret", v)}
              preview={paypal.secret_preview} configured={paypal.secret_configured}
              placeholder="EGE..." testId="paypal-secret" />
          </div>
          <div>
            <Label className="text-xs">Email da conta (receiver)</Label>
            <Input value={paypal.receiver_email || ""} onChange={(e) => setSection("paypal", "receiver_email", e.target.value)}
              placeholder="sales.global@eudactica.com" data-testid="paypal-receiver" />
          </div>
        </GatewayCard>

        {/* ifthenpay */}
        <GatewayCard
          title="ifthenpay"
          subtitle="Multibanco, MB WAY, Payshop, Cartões (PT)"
          accent="#f59e0b"
          enabled={ifthenpay.enabled}
          onToggleEnabled={(v) => setSection("ifthenpay", "enabled", v)}
          onTest={() => test("ifthenpay")}
          testTestId="ifthenpay"
        >
          <div>
            <Label className="text-xs">Gateway Key</Label>
            <SecretInput
              value={drafts["ifthenpay.gateway_key"] || ""} onChange={(v) => setDraftSecret("ifthenpay", "gateway_key", v)}
              preview={ifthenpay.gateway_key_preview} configured={ifthenpay.gateway_key_configured}
              placeholder="Backoffice ifthenpay → API keys" testId="ifthenpay-gateway" />
          </div>
          <div>
            <Label className="text-xs">Anti-Phishing Key</Label>
            <SecretInput
              value={drafts["ifthenpay.anti_phishing_key"] || ""} onChange={(v) => setDraftSecret("ifthenpay", "anti_phishing_key", v)}
              preview={ifthenpay.anti_phishing_key_preview} configured={ifthenpay.anti_phishing_key_configured}
              placeholder="Configura no callback URL do backoffice" testId="ifthenpay-anti-phishing" />
          </div>
          <div>
            <Label className="text-xs">MB WAY Key (opcional)</Label>
            <SecretInput
              value={drafts["ifthenpay.mbway_key"] || ""} onChange={(v) => setDraftSecret("ifthenpay", "mbway_key", v)}
              preview={ifthenpay.mbway_key_preview} configured={ifthenpay.mbway_key_configured}
              placeholder="Chave específica do MB WAY" testId="ifthenpay-mbway" />
          </div>
          <div>
            <Label className="text-xs">Multibanco Key (opcional)</Label>
            <SecretInput
              value={drafts["ifthenpay.multibanco_key"] || ""} onChange={(v) => setDraftSecret("ifthenpay", "multibanco_key", v)}
              preview={ifthenpay.multibanco_key_preview} configured={ifthenpay.multibanco_key_configured}
              placeholder="Chave para referências Multibanco" testId="ifthenpay-multibanco" />
          </div>
        </GatewayCard>
      </div>

      {/* Origin of active credentials */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="text-xs uppercase font-bold text-slate-700 mb-2">Origem das credenciais ativas</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-xs font-mono">
          {originRows.map(([k, src]) => (
            <div key={k} className="flex justify-between border-b border-slate-200 py-0.5">
              <span className="text-slate-600">{k}</span>
              <span className={`font-bold ${src === "DB" ? "text-emerald-700" : src === "ENV" ? "text-slate-500" : "text-slate-400"}`}>{src}</span>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          <strong>DB</strong> = configurada via admin. <strong>ENV</strong> = ainda vem do <code>.env</code>. Quando substituíres aqui, passa a <strong>DB</strong>.
        </p>
      </div>

      {/* Bank transfer */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-[Fredoka] font-bold text-lg">Transferência bancária</h3>
            <p className="text-xs text-slate-500">Aparece na página pública de pagamento como método sempre disponível.</p>
          </div>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={!!bank.enabled} onChange={(e) => setSection("bank_transfer", "enabled", e.target.checked)}
              className="w-4 h-4 accent-emerald-600" data-testid="bank-enabled" />
            <span className={`text-xs font-bold uppercase ${bank.enabled ? "text-emerald-700" : "text-slate-400"}`}>Ativo</span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Beneficiário</Label>
            <Input value={bank.beneficiary || ""} onChange={(e) => setSection("bank_transfer", "beneficiary", e.target.value)} data-testid="bank-beneficiary" />
          </div>
          <div>
            <Label className="text-xs">IBAN (PT)</Label>
            <Input value={bank.iban_pt || ""} onChange={(e) => setSection("bank_transfer", "iban_pt", e.target.value)} className="font-mono" data-testid="bank-iban-pt" />
          </div>
          <div>
            <Label className="text-xs">IBAN internacional</Label>
            <Input value={bank.iban_intl || ""} onChange={(e) => setSection("bank_transfer", "iban_intl", e.target.value)} className="font-mono" data-testid="bank-iban-intl" />
          </div>
          <div>
            <Label className="text-xs">SWIFT / BIC</Label>
            <Input value={bank.bic || ""} onChange={(e) => setSection("bank_transfer", "bic", e.target.value)} className="font-mono" data-testid="bank-bic" />
          </div>
        </div>
      </div>

      {/* Pay-later */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-[Fredoka] font-bold text-lg">Pagar mais tarde</h3>
            <p className="text-xs text-slate-500 max-w-xl">A encomenda fica reservada e o cliente recebe um email com link de pagamento. Defines o prazo do link e quantos lembretes enviamos por email.</p>
          </div>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={!!later.enabled} onChange={(e) => setSection("pay_later", "enabled", e.target.checked)}
              className="w-4 h-4 accent-emerald-600" data-testid="pay-later-enabled" />
            <span className={`text-xs font-bold uppercase ${later.enabled ? "text-emerald-700" : "text-slate-400"}`}>Ativo</span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Validade do link (dias)</Label>
            <Input type="number" min={1} max={90} value={later.token_days || 7}
              onChange={(e) => setSection("pay_later", "token_days", Number(e.target.value) || 7)}
              data-testid="pay-later-days" />
          </div>
          <div>
            <Label className="text-xs">Lembretes (dias após encomenda, separados por vírgula)</Label>
            <Input value={later.reminder_days_after || ""} onChange={(e) => setSection("pay_later", "reminder_days_after", e.target.value)}
              placeholder="1,3,7" data-testid="pay-later-reminders" />
            <p className="text-[10px] text-slate-500 mt-1">Cada cliente recebe um email no dia X, com X &gt; 0 e X &lt; 60. Vazio = sem lembretes.</p>
          </div>
        </div>
        <div className="mt-3">
          <Button variant="outline" onClick={runReminders} className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="btn-run-reminders">
            <Zap className="w-4 h-4 mr-1.5" /> Enviar lembretes agora
          </Button>
          <span className="text-xs text-slate-500 ml-3">
            Força o scheduler a correr agora (normalmente corre automaticamente a cada 15 minutos).
          </span>
        </div>
      </div>
    </div>
  );
}
